import React from 'react';
import { cn } from '../../lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: 'default' | 'filled';
  fullWidth?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({
    className,
    type = 'text',
    label,
    error,
    helperText,
    leftIcon,
    rightIcon,
    variant = 'default',
    fullWidth = false,
    id,
    ...props
  }, ref) => {

    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const baseClasses = "flex h-10 rounded-lg border px-3 py-2 text-body-medium text-gray-100 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#13EC5B]/50 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all";

    const variantClasses = {
      default: "border-white/10 bg-white/[0.05] hover:border-white/20 focus-visible:border-[#13EC5B]/40",
      filled: "border-transparent bg-white/[0.08] focus-visible:bg-white/[0.05]",
    };

    const errorClasses = error ? "border-red-500/50 focus-visible:ring-red-500/50" : "";
    const widthClasses = fullWidth ? "w-full" : "";
    const hasLeftIcon = leftIcon !== undefined;
    const hasRightIcon = rightIcon !== undefined;

    return (
      <div className={cn("space-y-2", fullWidth && "w-full")}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-label-medium font-medium text-gray-200"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
              {leftIcon}
            </div>
          )}

          <input
            type={type}
            id={inputId}
            className={cn(baseClasses, variantClasses[variant], errorClasses, widthClasses, hasLeftIcon && "pl-10", hasRightIcon && "pr-10", className)}
            ref={ref}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
              {rightIcon}
            </div>
          )}
        </div>

        {(error || helperText) && (
          <p className={cn("text-body-small", error ? "text-red-400" : "text-gray-500")}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
